import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Plus } from 'lucide-react';

/**
 * Asked. Answered. — an editorial split: the accordion on the right, and a
 * giant outlined numeral on the left that tracks whichever question is open,
 * morphing as the reader moves through the list. The page answers back.
 */

const faqs = [
  {
    q: 'Is STON.FIT free?',
    a: "Yes — right now, everything is free. Tracking, STON AI, lab insights, the works. As the platform grows, some advanced features may move to a paid tier — but the people who join early will always get the best deal we ever offer. Being early pays.",
  },
  {
    q: 'Who is this app for?',
    a: "Anyone who wants to become a healthier, better version of themselves. If you want to actually understand your body — food, training, sleep, recovery, even your blood work — STON.FIT is built for you. No fitness experience needed; day one is designed for beginners.",
  },
  {
    q: "I'm a professional — is this only for gym trainers?",
    a: "Not at all. Clinical nutritionists, dietitians, strength coaches — and the hybrid coaches who handle both training and diet — all run their practice on STON.FIT. Clients, plans, check-ins, payments and consultations, in one place instead of five apps.",
  },
  {
    q: 'Do coaches get a desktop version?',
    a: "Yes. The Coach Console runs full-screen on the web — manage clients, build plans and watch your revenue from a real screen on the days you work from a desk, then carry the same business in your pocket with the app.",
  },
  {
    q: 'Do I need a coach to use it?',
    a: "No. Track solo for as long as you like — the app reads your signals either way. And the day you decide you want expert eyes, you can hire a handpicked coach, nutritionist or dietitian right inside the app. Your data is already there when they say yes.",
  },
  {
    q: 'What can I actually track?',
    a: "Your complete health: nutrition with an Indian food database, water, caffeine, sleep, recovery, every lift, body measurements, progress photos — and lab reports translated into plain language. Everything connected, so it finally makes sense together.",
  },
];

const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <div className="relative border-b border-ston-border last:border-b-0">
    {/* Active rail */}
    <motion.span
      aria-hidden="true"
      className="absolute left-[-25px] md:left-[-41px] top-6 bottom-6 w-[3px] rounded-full bg-gradient-to-b from-ston-purple to-ston-purple-light"
      initial={false}
      animate={{ opacity: isOpen ? 1 : 0, scaleY: isOpen ? 1 : 0.3 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformOrigin: 'top', boxShadow: '0 0 12px rgba(168,85,247,0.7)' }}
    />

    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="group flex w-full items-center gap-4 md:gap-5 py-5 md:py-6 text-left"
      data-testid={`faq-${index}`}
    >
      <span
        className={`font-mono text-[11px] md:text-[12px] tracking-[2px] shrink-0 transition-colors duration-300 ${
          isOpen ? 'text-ston-purple-light' : 'text-ston-text-muted'
        }`}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <motion.span
        animate={{ x: isOpen ? 4 : 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className={`flex-1 font-outfit font-semibold text-[16px] md:text-[19px] leading-snug transition-colors duration-300 ${
          isOpen ? 'text-ston-text' : 'text-ston-text-secondary group-hover:text-ston-text'
        }`}
      >
        {faq.q}
      </motion.span>
      <motion.span
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
          isOpen
            ? 'border-ston-purple/60 bg-ston-purple/15 text-ston-purple-light'
            : 'border-ston-border text-ston-text-muted group-hover:border-ston-purple/40'
        }`}
      >
        <Plus size={15} />
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <motion.p
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="pb-6 pl-[30px] md:pl-[38px] pr-10 text-[15px] md:text-[16px] leading-relaxed text-ston-text-secondary max-w-[62ch]"
          >
            {faq.a}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQSection = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      className="relative py-24 md:py-32 bg-ston-bg-alt overflow-hidden"
      data-testid="faq-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-[-10%] w-[600px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.07), rgba(147,51,234,0.035) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.35fr] gap-10 lg:gap-16 items-start">
          {/* Left — headline + the living numeral */}
          <div className="lg:sticky lg:top-28 text-center lg:text-left">
            <motion.span
              className="section-label inline-block"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
            >
              ANSWERS
            </motion.span>
            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(30px, 4vw, 50px)', letterSpacing: '-0.025em' }}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="block text-ston-text">Asked.</span>
              <span className="block gradient-text">Answered.</span>
            </motion.h2>

            {/* The numeral that answers back */}
            <div
              aria-hidden="true"
              className="relative hidden lg:block mt-6 h-[220px] select-none pointer-events-none"
            >
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={open}
                  initial={{ opacity: 0, y: 46, rotateX: 40 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -46, rotateX: -40 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-0 top-0 font-outfit font-black italic leading-none"
                  style={{
                    fontSize: '220px',
                    letterSpacing: '-0.04em',
                    color: 'transparent',
                    WebkitTextStroke: '2.5px rgba(168,85,247,0.4)',
                  }}
                >
                  {open >= 0 ? String(open + 1).padStart(2, '0') : '?'}
                </motion.span>
              </AnimatePresence>
              {/* Soft fill glow behind the numeral */}
              <div
                className="absolute left-6 top-10 w-[220px] h-[160px] rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(closest-side, rgba(147,51,234,0.15), transparent 70%)',
                  }}
              />
            </div>

            <motion.p
              className="mt-8 lg:mt-4 text-[14px] text-ston-text-muted"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: 0.25 }}
            >
              Something else on your mind?{' '}
              <a
                href="https://www.instagram.com/ston.fit/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ston-purple-light hover:text-ston-text transition-colors"
              >
                DM @ston.fit
              </a>
              <br className="hidden lg:block" />
              <span className="lg:hidden"> or </span>
              <span className="hidden lg:inline">or write to </span>
              <a
                href="mailto:founder@ston.fit"
                className="text-ston-purple-light hover:text-ston-text transition-colors"
              >
                founder@ston.fit
              </a>
              .
            </motion.p>
          </div>

          {/* Right — the accordion */}
          <motion.div
            className="rounded-3xl border border-ston-border bg-white/[0.02] px-6 md:px-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.q}
                faq={faq}
                index={i}
                isOpen={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
